"use client";

import { useMemo } from "react";
import { useTranslations } from "next-intl";
import { Tour } from "nextstepjs";
import {
  RiCompassDiscoverLine,
  RiPushpinLine,
  RiRadarLine,
  RiShareCircleFill,
  RiUserLine,
  RiUserSmileLine,
} from "react-icons/ri";
import { StepSelector } from "../StepSelector";
import { POINTER_PADDING, POINTER_RADIUS } from "../constant";

interface UseOnboardingTourParams {
  tour: string;
  selectors: Record<string, StepSelector>;
}

export const onboardingStepsSelector: Record<string, StepSelector> = {
  step1: new StepSelector("small-sidebar"),
  step2: new StepSelector("fast-upload-resume"),
  step3: new StepSelector("dashboard-sidebar-item"),
  step4: new StepSelector("space-sidebar-item"),
  step5: new StepSelector("discover-sidebar-item"),
  step6: new StepSelector("large-sidebar"),
  step7: new StepSelector("sidebar-pin-toggle"),
  step8: new StepSelector("user-avatar"),
};

export const useOnboardingTour = ({
  tour,
  selectors,
}: UseOnboardingTourParams): Tour => {
  const t = useTranslations("sidebar.tour.onboarding");

  return useMemo(
    () => ({
      tour,
      steps: [
        {
          icon: <RiRadarLine />,
          title: t("step1.title"),
          showControls: true,
          showSkip: true,
          side: "right",
          selector: selectors.step1.selector,
          content: t("step1.content"),
          pointerPadding: POINTER_PADDING,
          pointerRadius: POINTER_RADIUS,
        },
        {
          icon: <RiShareCircleFill />,
          title: t("step2.title"),
          showControls: true,
          showSkip: true,
          side: "right",
          selector: selectors.step2.selector,
          content: t("step2.content"),
          pointerPadding: POINTER_PADDING,
          pointerRadius: POINTER_RADIUS,
        },
        {
          icon: <RiRadarLine />,
          title: t("step3.title"),
          showControls: true,
          showSkip: true,
          side: "right",
          selector: selectors.step3.selector,
          content: t("step3.content"),
          pointerPadding: POINTER_PADDING,
          pointerRadius: POINTER_RADIUS,
        },
        {
          icon: <RiUserSmileLine />,
          title: t("step4.title"),
          showControls: true,
          showSkip: true,
          side: "right",
          selector: selectors.step4.selector,
          content: t("step4.content"),
          pointerPadding: POINTER_PADDING,
          pointerRadius: POINTER_RADIUS,
        },
        {
          icon: <RiCompassDiscoverLine />,
          title: t("step5.title"),
          showControls: true,
          showSkip: true,
          side: "right",
          selector: selectors.step5.selector,
          content: t("step5.content"),
          pointerPadding: POINTER_PADDING,
          pointerRadius: POINTER_RADIUS,
        },
        {
          icon: <RiRadarLine />,
          title: t("step6.title"),
          showControls: true,
          showSkip: true,
          side: "right",
          selector: selectors.step6.selector,
          content: t("step6.content"),
          pointerPadding: POINTER_PADDING,
          pointerRadius: POINTER_RADIUS,
        },
        {
          icon: <RiPushpinLine />,
          title: t("step7.title"),
          showControls: true,
          showSkip: true,
          side: "bottom-left",
          selector: selectors.step7.selector,
          content: t("step7.content"),
          pointerPadding: POINTER_PADDING + 10,
          pointerRadius: POINTER_RADIUS,
        },
        {
          icon: <RiUserLine />,
          title: t("step8.title"),
          showControls: true,
          showSkip: true,
          side: "top-left",
          selector: selectors.step8.selector,
          content: t("step8.content"),
          pointerPadding: POINTER_PADDING,
          pointerRadius: POINTER_RADIUS,
        },
      ],
    }),
    [selectors, t, tour],
  );
};
